import React, { useMemo } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useSSETarget } from '../../common/useSseControl';
import { OWNER_CONSOLE_DISPLAY_CATEGORY, OWNER_CONSOLE_DISPLAY_LABEL } from './ownerConsoleDisplayMeta';
import useOwnerConsoleTimeSignals from './useOwnerConsoleTimeSignals';
import { buildBetDisplayItems, getAnswerColor } from '../room/betLabels';

const fallbackPhaseState = {
  displayCategory: OWNER_CONSOLE_DISPLAY_CATEGORY.UNRESOLVED,
  fallbackMessage: 'フェーズ未判定',
};

const panelCardSx = {
  p: { sm: 1, md: 1.15 },
  borderRadius: 3,
  bgcolor: 'rgba(15,23,42,0.78)',
  border: '1px solid rgba(148,163,184,0.18)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
};

const betRatioPanelSx = {
  ...panelCardSx,
  p: { sm: 1.25, md: 1.5 },
};

const clamp = (value, min = 0, max = 100) => Math.max(min, Math.min(max, value));
const MAX_VISIBLE_OPTIONS = 4;

const parseAnswers = (answers) => {
  if (Array.isArray(answers)) return answers;
  if (typeof answers !== 'string' || answers.trim() === '') return [];

  try {
    const parsed = JSON.parse(answers);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return answers.split(',').map((answer) => answer.trim()).filter(Boolean);
  }
};

const formatPoint = (value) => `${Number(value || 0).toLocaleString()} pt`;

const formatRate = (value) => {
  if (value === null || value === undefined || value === '' || value === '-') {
    return '-';
  }

  const rateValue = typeof value === 'object' ? (value.rate ?? value.optional_rate ?? value.base_rate) : value;
  if (rateValue === null || rateValue === undefined || rateValue === '' || rateValue === '-') {
    return '-';
  }

  const numericValue = Number(rateValue);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : String(rateValue);
};

const formatRemaining = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '--:--';
  const safeSeconds = Math.max(0, Math.ceil(Number(seconds)));
  const minutes = Math.floor(safeSeconds / 60).toString().padStart(2, '0');
  const restSeconds = (safeSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${restSeconds}`;
};

const getRoundSummaryTotalBet = (roundSummary = null) => Number(
  roundSummary?.totalBet ??
  0
);

const getResultSummaryTotalBet = (resultSummary = null) => Number(
  resultSummary?.all?.totalBet ??
  0
);

const ROUND_NOT_FORMED_REASON_LABEL = {
  no_bets: 'ベットが入らなかった',
  one_sided_bets: 'ベットが一方の選択肢に集中した',
  result_dispute_unresolved: '結果について判断が一致しなかった',
  member_invalidation_requested: 'ラウンドの無効希望が基準に達した',
};

const getRoundNotFormedReasonMessage = (reason) => {
  const reasonLabel = ROUND_NOT_FORMED_REASON_LABEL[reason] ?? '成立条件を満たさなかった';
  return `${reasonLabel}ために不成立となりました。`;
};

const buildBetOptions = (roundSummary = null, liveRates = null) => {
  const choices = parseAnswers(roundSummary?.answers).slice(0, MAX_VISIBLE_OPTIONS);
  const items = buildBetDisplayItems(choices);
  const rawSummary = Array.isArray(roundSummary?.betBySelection) ? roundSummary.betBySelection : [];
  const totalsBySelection = {};

  rawSummary.forEach((item) => {
    const selectionIndex = Number(item?.selection);
    if (Number.isInteger(selectionIndex)) {
      totalsBySelection[selectionIndex] = Number(item.totalBet || 0);
    }
  });

  const options = items.map((item) => ({
    selection: item.index,
    shortLabel: item.shortLabel,
    label: choices[item.index] || `選択肢 ${item.index + 1}`,
    total: Number(totalsBySelection[item.index] || 0),
    rate: formatRate(liveRates?.[item.index]),
    color: getAnswerColor(item.index).primary,
  }));
  const total = options.reduce((sum, option) => sum + option.total, 0);
  const equalPercent = options.length > 0 ? 100 / options.length : 0;
  const leadingTotal = Math.max(...options.map((option) => option.total), 0);
  const totalsDesc = [...new Set(options.map((option) => option.total).filter((amount) => amount > 0))]
    .sort((a, b) => b - a);

  return {
    hasBets: total > 0,
    total,
    options: options.map((option) => ({
      ...option,
      percent: total > 0 ? clamp((option.total / total) * 100) : 0,
      barPercent: total > 0 ? clamp((option.total / total) * 100) : equalPercent,
      isLeading: total > 0 && option.total > 0 && option.total === leadingTotal,
      popularityRank: option.total > 0 ? totalsDesc.indexOf(option.total) + 1 : null,
    })),
  };
};

function PhaseHeader({ event, phaseState, round, title, subtitle, tone = 'normal' }) {
  return (
    <Box sx={{ ...panelCardSx, background: tone === 'result' ? 'linear-gradient(135deg, rgba(251,191,36,0.24), rgba(15,23,42,0.86))' : panelCardSx.bgcolor }}>
      <Typography variant="h4" fontWeight={950} sx={{ lineHeight: 1.05, fontSize: 'clamp(1.5rem, 2vw, 2.2rem)' }}>
        {title || OWNER_CONSOLE_DISPLAY_LABEL[phaseState.displayCategory] || '状態取得中'}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
        <Chip size="small" label={`ROUND ${round?.number || event?.round || '-'}`} sx={{ bgcolor: 'rgba(14,165,233,0.18)', color: '#e0f2fe', fontWeight: 900 }} />
        <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.72)', fontWeight: 700 }}>
          {subtitle || phaseState.fallbackMessage || 'フェーズ情報を表示中'}
        </Typography>
      </Stack>
    </Box>
  );
}

function TimeProgressBar({ event, round }) {
  const timeSignals = useOwnerConsoleTimeSignals(round, undefined, event?.bet_close_mode);
  const isManualBetClose = event?.bet_close_mode === 'manual' && timeSignals?.countdownTarget !== 'start';
  const remainingRatio = timeSignals?.progressRatio === null || timeSignals?.progressRatio === undefined
    ? 0
    : clamp(timeSignals.progressRatio * 100);
  const ended = !isManualBetClose && (timeSignals?.timePhase === 'ended' || timeSignals?.countdownSeconds === 0);
  const displaySeconds = isManualBetClose ? timeSignals?.elapsedSeconds : timeSignals?.countdownSeconds;
  const level = timeSignals?.timerLevel;
  const barColor = ended ? '#64748b' : level === 'critical' ? '#ef4444' : level === 'warning' ? '#f59e0b' : '#22c55e';

  return (
    <Box sx={panelCardSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="caption" sx={{ color: '#bae6fd', fontWeight: 900 }}>
          {timeSignals?.countdownTarget === 'start' ? '開始まで' : (isManualBetClose ? '経過時間' : '残り時間')}
        </Typography>
        <Typography variant="h3" fontWeight={950} sx={{ letterSpacing: '0.02em', fontSize: 'clamp(2rem, 2.8vw, 3.2rem)' }}>
          {ended ? 'CLOSED' : formatRemaining(displaySeconds)}
        </Typography>
      </Stack>
      {!isManualBetClose && timeSignals?.showProgress ? (
        <Box sx={{ height: 18, mt: 1, borderRadius: 999, overflow: 'hidden', bgcolor: 'rgba(148,163,184,0.22)' }}>
          <Box sx={{ width: `${ended ? 0 : remainingRatio}%`, height: '100%', bgcolor: barColor, boxShadow: `0 0 18px ${barColor}`, transition: 'width 0.35s ease' }} />
        </Box>
      ) : null}
    </Box>
  );
}

function BetRatioBar({ event, round, fallbackRoundSummary = null, final = false, fillAvailable = false }) {
  const roundSummarySseData = useSSETarget('roundSummary');
  const roundRateSseData = useSSETarget('roundRate');
  const roundSummary = roundSummarySseData?.type === 'direct' &&
    roundSummarySseData.action === 'update' &&
    roundSummarySseData.target === 'roundSummary' &&
    Number(roundSummarySseData.payload?.round_id) === Number(round?.id)
    ? roundSummarySseData.payload
    : fallbackRoundSummary;
  const liveRates = roundRateSseData?.type === 'direct' &&
    roundRateSseData.action === 'update' &&
    roundRateSseData.target === 'roundRate' &&
    Array.isArray(roundRateSseData.payload)
    ? roundRateSseData.payload
    : null;
  const betSummary = useMemo(
    () => buildBetOptions(roundSummary, liveRates),
    [roundSummary, liveRates]
  );
  const { hasBets, options, total } = betSummary;
  const displayTotal = total || getRoundSummaryTotalBet(roundSummary);
  const useCompactGrid = options.length > 2;
  const fillOptionCards = fillAvailable && useCompactGrid;

  return (
    <Box
      sx={{
        ...betRatioPanelSx,
        ...(fillAvailable ? {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        } : {}),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.2, gap: 1 }}>
        <Typography variant="body2" sx={{ color: '#bae6fd', fontWeight: 950 }}>{final ? '最終ベット割合' : 'ベット割合'}</Typography>
        <Typography variant="body1" fontWeight={950} sx={{ color: '#f8fafc', textAlign: 'right' }}>総ベット {formatPoint(displayTotal)}</Typography>
      </Stack>
      <Box sx={{ display: 'flex', height: 30, overflow: 'hidden', borderRadius: 999, bgcolor: 'rgba(148,163,184,0.16)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {hasBets ? options.map((option) => (
          <Box key={option.selection} sx={{ width: `${option.barPercent}%`, bgcolor: option.color, boxShadow: `0 0 16px ${option.color}`, transition: 'width 0.35s ease' }} />
        )) : (
          <Box sx={{ width: '100%', bgcolor: 'repeating-linear-gradient(90deg, rgba(148,163,184,0.20) 0 10px, rgba(148,163,184,0.12) 10px 20px)' }} />
        )}
      </Box>
      {!hasBets ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'rgba(226,232,240,0.66)', fontWeight: 800 }}>
          {displayTotal > 0 ? '選択肢別の集計待ちです。' : 'まだベットはありません。'}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: useCompactGrid ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          gridTemplateRows: fillOptionCards ? 'repeat(2, minmax(0, 1fr))' : undefined,
          gap: 0.8,
          mt: 1,
          ...(fillOptionCards ? {
            flex: 1,
            minHeight: 0,
          } : {}),
        }}
      >
        {options.map((option) => (
          <Box
            key={option.selection}
            sx={{
              minWidth: 0,
              height: fillOptionCards ? '100%' : 'auto',
              display: fillOptionCards ? 'flex' : 'block',
              flexDirection: fillOptionCards ? 'column' : undefined,
              p: 0.9,
              borderRadius: 2,
              bgcolor: option.isLeading
                ? 'rgba(250,204,21,0.16)'
                : option.popularityRank === 2
                  ? 'rgba(255,255,255,0.075)'
                  : option.popularityRank === 3
                    ? 'rgba(255,255,255,0.058)'
                    : 'rgba(255,255,255,0.045)',
              border: option.isLeading
                ? '1px solid rgba(250,204,21,0.72)'
                : option.popularityRank === 2
                  ? '1px solid rgba(226,232,240,0.34)'
                  : option.popularityRank === 3
                    ? '1px solid rgba(148,163,184,0.26)'
                    : '1px solid rgba(148,163,184,0.14)',
              boxShadow: option.isLeading
                ? '0 0 0 1px rgba(250,204,21,0.14), 0 10px 22px rgba(250,204,21,0.10)'
                : option.popularityRank === 2
                  ? '0 8px 18px rgba(15,23,42,0.18)'
                  : 'none',
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1.25,
                  bgcolor: `${option.color}2e`,
                  border: `1px solid ${option.color}`,
                  color: '#f8fafc',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '0.86rem',
                  fontWeight: 950,
                  lineHeight: 1,
                  flexShrink: 0,
                  boxShadow: `0 0 12px ${option.color}66`,
                }}
              >
                {option.shortLabel}
              </Box>
              <Typography
                fontWeight={900}
                title={option.label}
                sx={{
                  color: '#f8fafc',
                  fontSize: '0.98rem',
                  lineHeight: 1.2,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Typography>
              {option.popularityRank ? (
                <Chip
                  size="small"
                  label={option.isLeading ? '最多' : `#${option.popularityRank}`}
                  sx={{
                    height: 22,
                    ml: 'auto',
                    flexShrink: 0,
                    bgcolor: option.isLeading ? 'rgba(250,204,21,0.92)' : 'rgba(148,163,184,0.22)',
                    color: option.isLeading ? '#1e293b' : '#e2e8f0',
                    fontWeight: 950,
                    '& .MuiChip-label': { px: 0.8, fontSize: '0.78rem' },
                  }}
                />
              ) : null}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 0.75, mt: fillOptionCards ? 'auto' : 0.75, minWidth: 0 }}>
              <Typography fontWeight={950} sx={{ color: option.color, flexShrink: 0, fontSize: '1.35rem', lineHeight: 1 }}>{option.percent.toFixed(0)}%</Typography>
              <Typography fontWeight={900} sx={{ color: 'rgba(226,232,240,0.86)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.95rem' }}>{formatPoint(option.total)}</Typography>
            </Stack>
            <Typography fontWeight={900} sx={{ display: 'block', mt: 0.45, color: 'rgba(226,232,240,0.78)', lineHeight: 1.15, fontSize: '0.92rem' }}>
              {option.rate === '-' ? '倍率 -' : `${option.rate}倍`}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const REVIEW_PANEL_TEXT = {
  label: '結果確認中',
  heldTitle: 'オーナーが内容を確認しています。',
  reviewingTitle: '参加者からの再確認を受け付けています。',
  heldDescription: '再確認依頼を受け、結果の扱いを確認中です。',
  reviewingDescription: 'お手元の端末から結果の変更を提案できます。問題なければこのままお待ちください。',
};

function ResultReviewMessagePanel({ round, review }) {
  const reviewStatus = review?.review_status ?? round?.review_status;
  const isHeld = reviewStatus === 'held';

  return (
    <Box sx={panelCardSx}>
      <Typography variant="caption" sx={{ color: '#bae6fd', fontWeight: 900 }}>
        {REVIEW_PANEL_TEXT.label}
      </Typography>
      <Typography variant="h5" fontWeight={950} sx={{ mt: 0.5, lineHeight: 1.3 }}>
        {isHeld ? REVIEW_PANEL_TEXT.heldTitle : REVIEW_PANEL_TEXT.reviewingTitle}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: 'rgba(226,232,240,0.72)', fontWeight: 800 }}>
        {isHeld ? REVIEW_PANEL_TEXT.heldDescription : REVIEW_PANEL_TEXT.reviewingDescription}
      </Typography>
    </Box>
  );
}

function RoundResultSummary({ resultSummary }) {
  const resultAll = resultSummary?.all ?? null;
  const isRoundNotFormed = Boolean(resultSummary?.round_not_formed);
  const choices = parseAnswers(resultSummary?.answers);
  const resultValue = resultSummary?.result;
  const resultKey = resultValue !== null && resultValue !== undefined
    ? String(resultValue)
    : '';
  const resultIndex = resultKey !== '' ? Number(resultKey) : null;
  const resultLabel = isRoundNotFormed
    ? 'ラウンド不成立'
    : resultKey !== ''
    ? choices[resultIndex] || `結果 ${resultKey}`
    : '結果取得待ち';
  const participants = Number(resultSummary?.all?.participantsCount ?? 0);
  const totalBet = getResultSummaryTotalBet(resultSummary);
  const top3 = Array.isArray(resultAll?.topResult) ? resultAll.topResult : [];
  const summaryLoaded = Boolean(resultAll);
  const notFormedMessage = getRoundNotFormedReasonMessage(resultSummary?.round_not_formed_reason);

  return (
    <Box sx={{ ...panelCardSx, background: 'linear-gradient(160deg, rgba(250,204,21,0.28), rgba(15,23,42,0.9) 58%)', border: '1px solid rgba(250,204,21,0.34)' }}>
      <Typography variant="caption" sx={{ color: '#fde68a', fontWeight: 900 }}>確定結果</Typography>
      <Typography variant="h3" fontWeight={950} sx={{ mt: 0.5, fontSize: 'clamp(1.9rem, 2.7vw, 3.1rem)' }}>{resultLabel}</Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip label={`参加 ${summaryLoaded ? participants : '-'}人`} sx={{ bgcolor: 'rgba(56,189,248,0.16)', color: '#e0f2fe', fontWeight: 900 }} />
        <Chip label={`総ベット ${summaryLoaded ? formatPoint(totalBet) : '取得中'}`} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 900 }} />
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'rgba(254,243,199,0.78)', fontWeight: 900 }}>
          {isRoundNotFormed ? 'ラウンド不成立' : '今回のトップ'}
        </Typography>
        {isRoundNotFormed ? (
          <Typography variant="body2" fontWeight={850} sx={{ mt: 0.35, color: 'rgba(254,243,199,0.86)' }}>
            {notFormedMessage}
          </Typography>
        ) : (
          (top3.length ? top3 : ['1位 表示待ち', '2位 表示待ち', '3位 表示待ち']).slice(0, 3).map((item, index) => (
            <Typography key={typeof item === 'string' ? item : `${item.name}-${index}`} variant="body2" fontWeight={850} sx={{ mt: 0.35 }}>
              #{index + 1} {typeof item === 'string' ? item : `${item.name ?? 'プレイヤー'} ${formatPoint(item.totalRefund ?? item.point ?? item.amount ?? 0)}`}
            </Typography>
          ))
        )}
      </Box>
    </Box>
  );
}

function ChatPreviewPanel({ chat, compact = false, event }) {
  const latestMessages = useMemo(() => (Array.isArray(chat?.messages) ? chat.messages.slice(-1) : []), [chat?.messages]);

  return (
    <Box sx={{ ...panelCardSx, minHeight: compact ? 136 : 176, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" sx={{ color: '#bae6fd', fontWeight: 900 }}>CHAT</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.52)', fontWeight: 800 }}>user messages</Typography>
      </Stack>
      <Stack spacing={0.75} sx={{ mt: 1, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {chat?.loading && latestMessages.length === 0 ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', borderRadius: 2, border: '1px dashed rgba(148,163,184,0.24)', color: 'rgba(226,232,240,0.56)', textAlign: 'center', px: 1 }}>
            <Typography variant="body2" fontWeight={800}>チャット取得中...</Typography>
          </Box>
        ) : null}
        {!chat?.loading && latestMessages.length === 0 ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', borderRadius: 2, border: '1px dashed rgba(148,163,184,0.24)', color: 'rgba(226,232,240,0.56)', textAlign: 'center', px: 1 }}>
            <Typography variant="body2" fontWeight={800}>まだチャットはありません</Typography>
          </Box>
        ) : null}
        {latestMessages.map((message) => {
          const isOwner = event?.owner !== undefined && event?.owner !== null
            ? String(message.user_id) === String(event.owner)
            : false;
          const answerColor = message.meta?.answer_color;
          const hitStreak = Number(message.meta?.hit_streak);
          return (
            <Box
              key={message.id}
              sx={{
                p: 0.75,
                borderRadius: 2,
                bgcolor: answerColor ? `${answerColor}33` : (isOwner ? 'rgba(34,211,238,0.14)' : 'rgba(255,255,255,0.07)'),
                border: answerColor ? `1px solid ${answerColor}` : (isOwner ? '1px solid rgba(103,232,249,0.24)' : '1px solid rgba(148,163,184,0.14)'),
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={950} sx={{ color: isOwner ? '#67e8f9' : '#e0f2fe', flexShrink: 0 }}>{isOwner ? 'OWNER' : ''}</Typography>
                <Typography variant="caption" fontWeight={850} sx={{ color: 'rgba(226,232,240,0.76)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{message.user_name || `ユーザー${message.user_id ?? ''}`}</Typography>
                {Number.isFinite(hitStreak) && hitStreak >= 2 ? (
                  <Chip
                    label={`${hitStreak}連続的中`}
                    size="small"
                    color="success"
                    sx={{ height: 18, flexShrink: 0, '& .MuiChip-label': { px: 0.65, fontSize: '0.68rem', fontWeight: 900 } }}
                  />
                ) : null}
              </Stack>
              <Typography variant="body2" fontWeight={800} sx={{ color: '#f8fafc', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: compact ? 1 : 2, WebkitBoxOrient: 'vertical', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{message.message}</Typography>
            </Box>
          );
        })}
        {chat?.fetchError ? <Typography variant="caption" sx={{ color: '#fca5a5', fontWeight: 800 }}>{chat.fetchError}</Typography> : null}
      </Stack>
    </Box>
  );
}

export default function OwnerConsoleRightPanel({ event, round, liveSummary, roundSummary, phaseState, chat, resultSummary, review }) {
  const safePhaseState = { ...fallbackPhaseState, ...(phaseState ?? {}) };
  const category = safePhaseState.displayCategory;
  const phaseViews = {
    [OWNER_CONSOLE_DISPLAY_CATEGORY.WAITING]: (
      <>
        <PhaseHeader event={event} phaseState={safePhaseState} round={round} title="参加受付中" subtitle="開始までしばらくお待ちください" />
        <TimeProgressBar event={event} round={round} />
        <Box sx={panelCardSx}><Typography variant="h6" fontWeight={950}>開始待ち</Typography><Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.68)' }}>開始時刻になるとベット受付表示へ切り替わります。</Typography></Box>
        <ChatPreviewPanel chat={chat} event={event} />
      </>
    ),
    [OWNER_CONSOLE_DISPLAY_CATEGORY.BETTING]: (
      <>
        <PhaseHeader event={event} phaseState={safePhaseState} round={round} title="ベット受付中" subtitle="受付中のオッズボード" />
        <TimeProgressBar event={event} round={round} />
        <BetRatioBar event={event} round={round} fallbackRoundSummary={roundSummary} fillAvailable />
      </>
    ),
    [OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_INPUT]: (
      <>
        <PhaseHeader event={event} phaseState={safePhaseState} round={round} title="ベット受付終了" subtitle="結果入力待ち" />
        <BetRatioBar event={event} round={round} fallbackRoundSummary={roundSummary} final fillAvailable />
      </>
    ),
    [OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW]: (
      <>
        <PhaseHeader event={event} phaseState={safePhaseState} round={round} title="結果確認中" subtitle="オーナー判定の確認フェーズ" />
        <ResultReviewMessagePanel round={round} review={review} />
        <ChatPreviewPanel chat={chat} event={event} />
      </>
    ),
    [OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT]: (
      <>
        <PhaseHeader event={event} phaseState={safePhaseState} round={round} title="ラウンド結果" subtitle="結果確定" tone="result" />
        <RoundResultSummary resultSummary={resultSummary} />
        <ChatPreviewPanel chat={chat} event={event} compact />
      </>
    ),
    [OWNER_CONSOLE_DISPLAY_CATEGORY.COMPLETED]: (
      <>
        <PhaseHeader event={event} phaseState={safePhaseState} round={round} title="ルーム終了" subtitle="最終結果を確認できます" tone="result" />
        <Box sx={panelCardSx}><Typography variant="h6" fontWeight={950}>開催終了済み</Typography><Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.68)' }}>このルームは終了しました。お手元の端末から結果を確認できます。</Typography></Box>
        <ChatPreviewPanel chat={chat} event={event} />
      </>
    ),
  };

  return (
    <Box component="aside" sx={{ gridArea: 'rightPanel', minHeight: 0, p: { sm: 1, md: 1.15 }, borderRadius: 3, bgcolor: 'rgba(2, 6, 23, 0.92)', border: '1px solid rgba(56, 189, 248, 0.26)', boxShadow: '-10px 0 24px rgba(0,0,0,0.26)', color: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {phaseViews[category] ?? (
          <>
            <PhaseHeader event={event} phaseState={safePhaseState} round={round} title="データ取得中" subtitle="しばらくお待ちください。" />
            <ChatPreviewPanel chat={chat} event={event} />
          </>
        )}
      </Stack>
    </Box>
  );
}
